import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  XMarkIcon, 
  ArrowPathIcon, 
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { useTemplateRaciAssignments } from '../../hooks/useTemplateRaciAssignments';

// RACI role descriptions for tooltips
const RACI_DESCRIPTIONS = {
  R: 'Responsible: Person who does the work',
  A: 'Accountable: Person with decision authority',
  C: 'Consulted: Person providing input before work',
  I: 'Informed: Person receiving updates on progress'
};

// RACI role colors
const RACI_COLORS = {
  R: 'bg-green-100 text-green-800 border-green-300',
  A: 'bg-blue-100 text-blue-800 border-blue-300',
  C: 'bg-purple-100 text-purple-800 border-purple-300',
  I: 'bg-yellow-100 text-yellow-800 border-yellow-300'
};

// Get the full name of a role type
const getRoleName = (roleType) => {
  const roleNames = {
    R: 'Responsible',
    A: 'Accountable',
    C: 'Consulted',
    I: 'Informed'
  };
  return roleNames[roleType] || roleType;
};

const TemplateRaciAssignments = ({ entityType, entityId, readOnly = false }) => {
  const [users, setUsers] = useState([]);
  const [addingRole, setAddingRole] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [entityForRole, setEntityForRole] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});
  
  // Get the project ID for the current entity (for context)
  const [projectId, setProjectId] = useState(null);
  const [loadingAssignment, setLoadingAssignment] = useState(false);

  // State for phases and steps data
  const [phases, setPhases] = useState([]);
  const [steps, setSteps] = useState({});
  
  // Use the template RACI assignments hook
  const { 
    getAssignments,
    updateAssignment,
    fetchAllAssignments,
    isLoading,
    error,
    // Direct access to assignment data
    assignments: templateAssignments,
    phases: phaseAssignments,
    steps: stepAssignments
  } = useTemplateRaciAssignments(projectId);
  
  // Calculate assignments based on the entity type and ID
  const assignments = getAssignments(entityType, entityId);
  
  // Ensure all RACI arrays exist
  const safeAssignments = {
    R: Array.isArray(assignments.R) ? assignments.R : [],
    A: Array.isArray(assignments.A) ? assignments.A : [],
    C: Array.isArray(assignments.C) ? assignments.C : [],
    I: Array.isArray(assignments.I) ? assignments.I : []
  };

  // Toggle phase expanded state
  const togglePhaseExpanded = (phaseId) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };

  // Get the count of assigned users for a role
  const getAssignedCount = (entityType, entityId, roleType) => {
    const assignments = getAssignments(entityType, entityId);
    return Array.isArray(assignments[roleType]) ? assignments[roleType].length : 0;
  };
  
  // Get the project ID for an entity
  const getProjectIdForEntity = useCallback(async () => {
    console.log(`Getting project ID for ${entityType}:${entityId}`);
    
    try {
      if (!entityType || !entityId || entityId === 'undefined') {
        console.error('Invalid entity information:', { entityType, entityId });
        return null;
      }

      if (entityType === 'project') {
        // For projects, just use the entity ID as the template ID
        console.log(`Entity is a project, using ID ${entityId} as template ID`);
        setProjectId(entityId);
        return entityId;
      } else if (entityType === 'phase') {
        const phaseResponse = await axios.get(`/api/phases/${entityId}`);
        if (phaseResponse.data && phaseResponse.data.phase && phaseResponse.data.phase.project_id) {
          const phaseProjectId = phaseResponse.data.phase.project_id;
          console.log(`Found project ID ${phaseProjectId} from phase data`);
          setProjectId(phaseProjectId);
          return phaseProjectId;
        }
      } else if (entityType === 'step') {
        const stepResponse = await axios.get(`/api/steps/${entityId}`);
        
        if (stepResponse.data && stepResponse.data.step) {
          const stepData = stepResponse.data.step;
          
          // Try to get project_id directly from step data
          if (stepData.project_id) {
            console.log(`Found project_id in step data: ${stepData.project_id}`);
            setProjectId(stepData.project_id);
            return stepData.project_id;
          }
          
          // If needed, get project ID from phase
          if (stepData.phase_id) {
            const phaseResponse = await axios.get(`/api/phases/${stepData.phase_id}`);
            if (phaseResponse.data && phaseResponse.data.phase && phaseResponse.data.phase.project_id) {
              const stepProjectId = phaseResponse.data.phase.project_id;
              console.log(`Found project ID ${stepProjectId} via phase`);
              setProjectId(stepProjectId);
              return stepProjectId;
            }
          }
        }
      }
      
      console.error('Could not determine project ID for entity:', { entityType, entityId });
      return null;
    } catch (err) {
      console.error('Error getting project ID for entity:', err);
      return null;
    }
  }, [entityType, entityId]);

  // Fetch phases and steps for this project
  const fetchPhasesAndSteps = useCallback(async (projectId) => {
    if (!projectId) return;
    
    try {
      const response = await axios.get(`/api/projects/templates/${projectId}`);
      if (response.data && response.data.template) {
        const projectData = response.data.template;
        
        if (Array.isArray(projectData.phases)) {
          setPhases(projectData.phases);
          
          // Process steps by phase
          const stepsMap = {};
          projectData.phases.forEach(phase => {
            if (Array.isArray(phase.steps)) {
              stepsMap[phase.id] = phase.steps;
            }
          });
          setSteps(stepsMap);
        }
      }
    } catch (err) {
      console.error('Error fetching phases and steps:', err);
    }
  }, []);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/users');
      if (response.data && Array.isArray(response.data.users)) {
        setUsers(response.data.users);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Failed to fetch users');
    }
  }, []);

  // Handle toggle assignment
  const handleToggleAssignment = useCallback(async (entityType, entityId, userId, roleType, isRemove = false) => {
    const stringUserId = String(userId);
    console.log(`${isRemove ? 'Removing' : 'Adding'} assignment:`, { 
      entityType, entityId, userId: stringUserId, roleType
    });
    
    // Validate input parameters
    if (!userId || !roleType || !entityType || !entityId) {
      console.error('Missing required parameters for toggle assignment');
      toast.error('Cannot update assignment: Missing required parameters');
      return;
    }
    
    // Set loading state
    setLoadingAssignment(true);
    
    try {
      // Call the updateAssignment function from the hook
      const success = await updateAssignment(
        entityType, 
        entityId, 
        roleType, 
        stringUserId, 
        !isRemove // isAdding = !isRemove
      );
      
      if (success) {
        // Show success message
        toast.success(`User ${isRemove ? 'removed from' : 'assigned to'} ${getRoleName(roleType)} role`);
      } else {
        toast.error(`Failed to ${isRemove ? 'remove' : 'add'} assignment`);
      }
    } catch (err) {
      console.error('Error toggling assignment:', err);
      toast.error(`Failed to ${isRemove ? 'remove' : 'add'} assignment: ${err.message || 'Unknown error'}`);
    } finally {
      setLoadingAssignment(false);
    }
  }, [updateAssignment]);

  // Add new assignment
  const handleAddAssignment = useCallback(async () => {
    if (!addingRole || !selectedUserId || !entityForRole) {
      toast.error('Please select a user');
      return;
    }
    
    await handleToggleAssignment(
      entityForRole.type, 
      entityForRole.id, 
      selectedUserId, 
      addingRole, 
      false
    );
    
    // Reset form
    setAddingRole(null);
    setEntityForRole(null);
    setSelectedUserId('');
  }, [addingRole, selectedUserId, entityForRole, handleToggleAssignment]);

  // Open modal to add user to a role for an entity
  const openAddUserModal = (entityType, entityId, roleType) => {
    setEntityForRole({ type: entityType, id: entityId });
    setAddingRole(roleType);
    setSelectedUserId('');
  };

  // Init component on mount
  useEffect(() => {
    let isMounted = true;
    
    const initComponent = async () => {
      if (!entityType || !entityId) {
        return;
      }
      
      try {
        // Get the project ID for this entity
        const templateProjectId = await getProjectIdForEntity();
        
        if (!templateProjectId) {
          toast.error('Could not determine template project ID');
          return;
        }
        
        // Fetch users
        await fetchUsers();
        
        // Fetch phases and steps
        await fetchPhasesAndSteps(templateProjectId);
      } catch (err) {
        console.error('Error initializing component:', err);
        toast.error('Error initializing RACI assignments');
      }
    };
    
    initComponent();
    
    return () => {
      isMounted = false;
    };
  }, [entityType, entityId, getProjectIdForEntity, fetchUsers, fetchPhasesAndSteps]);

  // Get available users for assignment
  const getAvailableUsers = useCallback(() => {
    if (!addingRole || !entityForRole) return [];
    
    // Get current assignments for this entity and role
    const currentAssignments = getAssignments(entityForRole.type, entityForRole.id);
    
    // Get IDs of users already assigned to this role
    const assignedUserIds = Array.isArray(currentAssignments[addingRole]) 
      ? currentAssignments[addingRole]
          .map(user => String(user.user_id || user.id))
          .filter(Boolean)
      : [];
    
    // Filter out users who are already assigned
    return users.filter(user => !assignedUserIds.includes(String(user.id)));
  }, [addingRole, entityForRole, getAssignments, users]);

  // Component for rendering a role cell
  const RoleCell = ({ entityType, entityId, roleType }) => {
    const [showAssignees, setShowAssignees] = useState(false);
    const assignments = getAssignments(entityType, entityId);
    const roleAssignments = Array.isArray(assignments[roleType]) ? assignments[roleType] : [];
    
    // Role colors
    const colors = {
      R: 'bg-green-100 text-green-800',
      A: 'bg-blue-100 text-blue-800',
      C: 'bg-purple-100 text-purple-800',
      I: 'bg-yellow-100 text-yellow-800'
    };
    
    // If there are no assignments, show a plus button
    if (roleAssignments.length === 0) {
      return (
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openAddUserModal(entityType, entityId, roleType);
          }}
          disabled={readOnly}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-dashed border-gray-300 text-gray-400 hover:text-gray-500 hover:border-gray-400"
          title={`Add ${getRoleName(roleType)}`}
          type="button"
        >
          <PlusIcon className="w-4 h-4" />
        </button>
      );
    }
    
    // If there are assignments, show the count
    return (
      <div className="flex items-center justify-center gap-1 relative">
        <button 
          className={`flex items-center justify-center w-6 h-6 rounded-full font-medium ${colors[roleType]}`}
          title={roleAssignments.map(u => u.user_name || u.name || `User ${u.user_id || u.id}`).join(', ')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowAssignees(!showAssignees);
          }}
          type="button"
        >
          {roleAssignments.length}
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openAddUserModal(entityType, entityId, roleType);
          }}
          disabled={readOnly}
          className="text-gray-400 hover:text-gray-600"
          title={`Add ${getRoleName(roleType)}`}
          type="button"
        >
          <PlusIcon className="w-4 h-4" />
        </button>

        {/* Dropdown for showing assigned users */}
        {showAssignees && (
          <div className="absolute z-10 mt-1 top-full left-0 w-48 bg-white shadow-lg rounded-md border border-gray-200">
            <div className="py-1 px-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
              <span className="text-xs font-medium text-gray-700">{getRoleName(roleType)} Users</span>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowAssignees(false);
                }}
                className="text-gray-500 hover:text-gray-700"
                type="button"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
            <ul className="py-1 max-h-48 overflow-y-auto">
              {roleAssignments.map(user => (
                <li key={user.user_id || user.id} className="px-2 py-1 hover:bg-gray-50 flex justify-between items-center">
                  <span className="text-sm">{user.user_name || user.name || `User ${user.user_id || user.id}`}</span>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleToggleAssignment(
                        entityType, 
                        entityId, 
                        user.user_id || user.id, 
                        roleType, 
                        true // isRemove = true
                      );
                      setShowAssignees(false);
                    }}
                    disabled={readOnly || loadingAssignment}
                    className="text-red-400 hover:text-red-600"
                    title="Remove assignment"
                    type="button"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  // Render component
  if (!entityType || !entityId) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-600">Missing required properties: entityType and entityId</p>
      </div>
    );
  }

  if (isLoading && Object.values(safeAssignments).every(arr => arr.length === 0)) {
    return (
      <div className="flex flex-col justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500 mb-4"></div>
        <span>Loading assignments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <h3 className="text-md font-medium text-red-800 mb-2">RACI Assignments</h3>
        <p className="text-sm text-red-600 mb-2">{error}</p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => fetchAllAssignments()}
            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <ArrowPathIcon className="h-4 w-4 mr-1" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white overflow-hidden sm:rounded-lg">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th className="py-1 px-1 w-4 text-xs font-normal text-center text-gray-400"></th>
              <th className="py-1 px-1 w-10 text-xs font-normal text-center text-green-600">R</th>
              <th className="py-1 px-1 w-10 text-xs font-normal text-center text-blue-600">A</th>
              <th className="py-1 px-1 w-10 text-xs font-normal text-center text-purple-600">C</th>
              <th className="py-1 px-1 w-10 text-xs font-normal text-center text-yellow-600">I</th>
            </tr>
          </thead>
          <tbody>
            {/* Single compact row for RACI roles */}
            <tr>
              <td className="py-1 px-1"></td>
              <td className="py-1 px-1 text-center">
                <RoleCell entityType={entityType} entityId={entityId} roleType="R" />
              </td>
              <td className="py-1 px-1 text-center">
                <RoleCell entityType={entityType} entityId={entityId} roleType="A" />
              </td>
              <td className="py-1 px-1 text-center">
                <RoleCell entityType={entityType} entityId={entityId} roleType="C" />
              </td>
              <td className="py-1 px-1 text-center">
                <RoleCell entityType={entityType} entityId={entityId} roleType="I" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Modal for adding a new assignment */}
      {addingRole && entityForRole && !readOnly && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity" 
              aria-hidden="true" 
              onClick={() => {
                setAddingRole(null);
                setEntityForRole(null);
              }}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            {/* Modal content */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Add {getRoleName(addingRole)} User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {entityForRole.type.charAt(0).toUpperCase() + entityForRole.type.slice(1)}: {
                          entityForRole.type === 'project' ? 'Template' :
                          entityForRole.type === 'phase' ? phases.find(p => p.id === entityForRole.id)?.name :
                          steps[phases.find(p => steps[p.id]?.find(s => s.id === entityForRole.id))?.id]?.find(s => s.id === entityForRole.id)?.name
                        }
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {RACI_DESCRIPTIONS[addingRole]}
                      </p>
                    </div>
                    
                    <div className="mt-4">
                      <label htmlFor="user-select" className="block text-sm font-medium text-gray-700">
                        Select User
                      </label>
                      <select
                        id="user-select"
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                      >
                        <option value="">Select a user</option>
                        {getAvailableUsers().map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddAssignment}
                  disabled={!selectedUserId || isLoading || loadingAssignment}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm ${
                    (!selectedUserId || isLoading || loadingAssignment) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  Add Assignment
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddingRole(null);
                    setEntityForRole(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateRaciAssignments;